import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  connectImapMailbox,
  deleteMailConnection,
  getMailConnections,
  getMailProviders,
  getMailThread,
  searchMail,
} from '@/services/api/mail.service'
import { useAuthStore } from '@/stores/auth.store'
import { useWorkspaceStore } from '@/stores/workspace.store'

/** Query keys live under the workspace so switching workspaces never shows another workspace's mailboxes. */
export const mailKeys = {
  providers: ['mail', 'providers'] as const,
  connections: (ws: string | null) => ['workspace', ws, 'mail', 'connections'] as const,
  search: (ws: string | null, connectionId: string, q: string) =>
    ['workspace', ws, 'mail', 'search', connectionId, q] as const,
  thread: (ws: string | null, connectionId: string, threadId: string) =>
    ['workspace', ws, 'mail', 'thread', connectionId, threadId] as const,
}

export function useMailProviders() {
  const accessToken = useAuthStore((s) => s.accessToken)
  return useQuery({ queryKey: mailKeys.providers, queryFn: getMailProviders, enabled: !!accessToken, staleTime: 5 * 60 * 1000 })
}

/** The user's own mailboxes, plus the workspace's when they may use them. */
export function useMailConnections() {
  const ws = useWorkspaceStore((s) => s.currentWorkspaceId)
  const accessToken = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: mailKeys.connections(ws),
    queryFn: getMailConnections,
    enabled: !!accessToken && !!ws,
    staleTime: 30 * 1000,
  })
}

export function useConnectImap() {
  const ws = useWorkspaceStore((s) => s.currentWorkspaceId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: connectImapMailbox,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mailKeys.connections(ws) }),
  })
}

export function useDeleteMailConnection() {
  const ws = useWorkspaceStore((s) => s.currentWorkspaceId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteMailConnection,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mailKeys.connections(ws) }),
  })
}

/** Newest-first search of one mailbox; each further page is fetched on demand. */
export function useMailSearch(connectionId: string | null, q: string) {
  const ws = useWorkspaceStore((s) => s.currentWorkspaceId)
  return useInfiniteQuery({
    queryKey: mailKeys.search(ws, connectionId ?? '', q),
    queryFn: ({ pageParam }) => searchMail(connectionId as string, q, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
    enabled: !!connectionId,
    staleTime: 30 * 1000,
  })
}

export function useMailThread(connectionId: string | null, threadId: string | null) {
  const ws = useWorkspaceStore((s) => s.currentWorkspaceId)
  return useQuery({
    queryKey: mailKeys.thread(ws, connectionId ?? '', threadId ?? ''),
    queryFn: () => getMailThread(connectionId as string, threadId as string),
    enabled: !!connectionId && !!threadId,
    staleTime: 30 * 1000,
  })
}
